import React from 'react';
import DataTable from '../../components/datatable/DataTable';
import { translate } from 'react-i18next';
import { Button, ButtonGroup } from 'react-bootstrap';

class SCControlPanelTable extends React.Component {
  static isNonRectangularShape(device) {
    if (!device) {
      return false;
    }
    if (device.shape === 'polygon' || device.shape === 'circle') {
      return true;
    }
    return !!(device.vertices && device.vertices.length >= 3);
  }

  static isNonRectangularWindow(windowMode, window) {
    if (windowMode === 'polygon' || windowMode === 'circle') {
      return true;
    }
    return !!(window && window.vertices && window.vertices.length >= 3);
  }

  static convertToDevConfig(dev) {
    if (dev.vertices && dev.vertices.length >= 3) {
      const result = { vertices: dev.vertices };
      if (dev.shape) {
        result.shape = dev.shape;
      }
      if (dev.radius !== undefined) {
        result.radius = dev.radius;
      }
      if (dev.xAngle !== undefined) {
        result.xAngle = dev.xAngle;
      }
      if (dev.angle !== undefined) {
        result.angle = dev.angle;
      }
      if (dev.origin) {
        result.origin = dev.origin;
      } else if (dev.x !== undefined || dev.y !== undefined || dev.z !== undefined) {
        result.origin = {
          x: dev.x || 0,
          y: dev.y || 0,
          z: dev.z || 0,
        };
      }
      if (dev.azimuth !== undefined) {
        result.azimuth = dev.azimuth;
      }
      return result;
    }
    return {
      origin: {
        x: dev.x,
        y: dev.y,
        z: dev.z,
      },
      size: {
        width: dev.width,
        height: dev.height === undefined ? dev.length : dev.height,
      },
      xAngle: dev.angle,
      shape: dev.shape,
      radius: dev.radius,
      azimuth: dev.azimuth,
    };
  }

  static deviceToFlat(device) {
    if (!device) {
      return {};
    }
    if (device.origin) {
      return {
        x: device.origin.x,
        y: device.origin.y,
        z: device.origin.z,
        width: device.size && device.size.width,
        height: device.size && device.size.height,
        angle: device.xAngle,
        azimuth: device.azimuth,
        vertices: device.vertices,
        shape: device.shape,
        radius: device.radius,
      };
    }
    return { ...device };
  }

  static getConfigForDevice(type, windowWidth, windowHeight) {
    const defaultDeviceConfig = {
      origin: {
        x: 0,
        y: 0,
        z: 0,
      },
      size: {
        width: windowWidth,
        height: windowHeight,
      },
      isShow: true,
      isHighLight: false,
    };

    switch (type) {
      case 'right':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: 0,
            y: windowWidth,
            z: 0,
          },
        };
      case 'top':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: 0,
            y: 0,
            z: windowHeight,
          },
          xAngle: 75,
        };
      case 'front':
        return {
          ...defaultDeviceConfig,
          origin: {
            x: windowHeight,
            y: 0,
            z: windowHeight,
          },
          xAngle: 10,
        };
      default:
        return defaultDeviceConfig;
    }
  }

  constructor(props) {
    super(props);

    const { t } = props;

    this.columns = [
      {
        Header: t('name'),
        accessor: 'name',
      },
      {
        Header: `X (${t('meter abbrv')})`,
        accessor: 'origin.x',
        Cell: ({ index, value }) => this.renderCell(index, 'x', value, {}),
      },
      {
        Header: `Y (${t('meter abbrv')})`,
        accessor: 'origin.y',
        Cell: ({ index, value }) => this.renderCell(index, 'y', value, {}),
      },
      {
        Header: `Z (${t('meter abbrv')})`,
        accessor: 'origin.z',
        Cell: ({ index, value }) => this.renderCell(index, 'z', value, {}),
      },
      {
        Header: `${t('width')} (${t('meter abbrv')})`,
        accessor: 'size.width',
        Cell: ({ index, value }) => this.renderSizeCell(index, 'width', value, { min: 0.01 }),
      },
      {
        Header: `${t('height')} (${t('meter abbrv')})`,
        accessor: 'size.height',
        Cell: ({ index, value }) => this.renderSizeCell(index, 'height', value, { min: 0.01 }),
      },
      {
        Header: `${t('angle')} (°)`,
        accessor: 'xAngle',
        Cell: ({ index, value }) => this.renderCell(index, 'angle', value, { min: 0, max: 90 }),
      },
      {
        Header: `${t('azimuth')} (°)`,
        accessor: 'azimuth',
        Cell: ({ index, value }) => this.renderAzimuthCell(index, value),
      },
    ];

    const windowVertices = (props.window && props.window.vertices) || [];
    const windowMode = props.windowMode
      || (windowVertices.length >= 3 ? 'polygon' : 'rectangle');
    const windowWidth = props.window.width
      || (windowVertices.length
        ? windowVertices.reduce((m, p) => Math.max(m, parseFloat(p.x || 0)), 0)
        : 0);
    const windowHeight = props.window.height
      || (windowVertices.length
        ? windowVertices.reduce((m, p) => Math.max(m, parseFloat(p.y || 0)), 0)
        : 0);
    const windowShape = windowMode === 'circle'
      ? 'circle'
      : (windowVertices.length >= 3 ? 'polygon' : 'rectangle');

    this.state = {
      list: [
        { name: 'window', index: 0 },
        ...(props.shadingDevices.left || []).map((item, index) => ({ name: 'leftDevices', index})),
        ...(props.shadingDevices.right || []).map((item, index) => ({ name: 'rightDevices', index})),
        ...(props.shadingDevices.top || []).map((item, index) => ({ name: 'topDevices', index})),
        ...(props.shadingDevices.front || []).map((item, index) => ({ name: 'frontDevices', index})),
      ],
      window: [{
        name: 'Window',
        shape: windowShape,
        vertices: windowVertices.length >= 3 ? windowVertices : undefined,
        size: { width: windowWidth, height: windowHeight },
      }],
      leftDevices: props.shadingDevices.left || [],
      rightDevices: props.shadingDevices.right || [],
      topDevices: props.shadingDevices.top || [],
      frontDevices: props.shadingDevices.front || [],
    };

    this.creationCount = [
      ...this.state.leftDevices,
      ...this.state.rightDevices,
      ...this.state.topDevices,
      ...this.state.frontDevices,
    ]
      .map(dev => parseInt(dev.name.split(' ')[1], 10))
      .reduce((maxId, id) => Math.max(maxId, id), 0);
  }

  componentDidMount() {
    if (this.props.onReady) {
      this.props.onReady(this);
    }
  }

  renderAzimuthCell(index, initialValue) {
    const item = this.state.list[index];
    const device = this.state[item.name][item.index];
    const isEnabled = item.name === 'leftDevices' || item.name === 'rightDevices';
    return (
      <input
        style={{ width: '100%', textAlign: 'right' }}
        disabled={!isEnabled}
        type="number"
        min={-360}
        max={360}
        defaultValue={initialValue}
        onChange={({ target: { value } }) => {
          if (isEnabled) {
            this.changeDevice(item.name, device.name, this.convertParam(device, 'azimuth', value));
          }
        }}
        onFocus={() => isEnabled && this.focusDevice(device.name)}
        onBlur={() => isEnabled && this.focusDevice(null)}
      />
    );
  }

  isSizeInputDisabled(item, device) {
    if (item.name === 'window') {
      return SCControlPanelTable.isNonRectangularWindow(this.props.windowMode, device);
    }
    return SCControlPanelTable.isNonRectangularShape(device);
  }

  renderSizeCell(index, name, initialValue, props) {
    const item = this.state.list[index];
    const device = this.state[item.name][item.index];
    return this.renderCell(
      index,
      name,
      initialValue,
      props,
      this.isSizeInputDisabled(item, device),
    );
  }

  renderCell(index, name, initialValue, props, forceDisabled = false) {
    const item = this.state.list[index];
    const device = this.state[item.name][item.index];
    const disabled = forceDisabled || initialValue === undefined;
    return (
      <input
        {...props}
        style={{ width: '100%', textAlign: 'right' }}
        disabled={disabled}
        type="number"
        defaultValue={initialValue}
        onChange={({ target: { value } }) => {
          if (!disabled) {
            this.changeDevice(item.name, device.name, this.convertParam(device, name, value));
          }
        }}
        onFocus={() => this.focusDevice(device.name)}
        onBlur={() => this.focusDevice(null)}
      />
    );
  }

  convertParam(currentParams, name, newValue) {
    let template;
    if (name === 'window') {
      template = {
        width: currentParams.size.width,
        height: currentParams.size.height,
      };
    } else {
      template = {
        x: currentParams.origin.x,
        y: currentParams.origin.y,
        z: currentParams.origin.z,
        width: currentParams.size.width,
        height: currentParams.size.height,
        angle: currentParams.xAngle,
        azimuth: currentParams.azimuth,
      };
    }

    return {
      ...template,
      [name]: parseFloat(newValue),
    };
  }

  addDevice(deviceType, stateKey) {
    this.creationCount++;
    const window = this.state.window[0];
    const addedDev = {
      ...SCControlPanelTable.getConfigForDevice(deviceType, window.size.width, window.size.height),
      name: `Device ${this.creationCount}`,
    };

    this.setState(
      {
        [stateKey]: [...this.state[stateKey], addedDev],
        list: [
          ...this.state.list,
          { name: stateKey, index: this.state[stateKey].length },
        ],
      },
      () => this.notifyDataChanged(),
    );
  }

  changeDevice(stateKey, name, data) {
    const newList = this.state[stateKey].map(item => {
      if (item.name !== name) {
        return item;
      }
      const flat = SCControlPanelTable.deviceToFlat(item);
      const merged = {
        ...flat,
        ...data,
        vertices: item.vertices,
        shape: item.shape,
        radius: item.radius,
      };
      return { ...item, ...SCControlPanelTable.convertToDevConfig(merged) };
    });
    this.setState(
      {
        [stateKey]: newList,
      },
      () => this.notifyDataChanged(),
    );
  }

  removeDevice(listIndex) {
    let item = this.state.list[listIndex];
    this.setState(
      {
        list: this.state.list.filter((item, index) => listIndex !== index),
        [item.name]: this.state[item.name].filter((_, index) => index !== item.index),
      },
      () => this.notifyDataChanged(),
    );
  }

  focusDevice(name) {
    const updatedDevices = Object.keys(this.state)
      .filter(key => key.toLowerCase().indexOf('devices') !== -1)
      .reduce((acc, deviceType) => {
        const newList = this.state[deviceType].map(device => ({ ...device, isHighLight: device.name === name}));
        return { ...acc, [deviceType]: newList };
      }, {});

    this.setState(updatedDevices, () => this.notifyDataChanged());
  }

  notifyDataChanged() {
    const { onDataChanged } = this.props;
    if (onDataChanged) {
      const renderData = Object.keys(this.state)
        .filter(key => key.toLowerCase().indexOf('devices') !== -1)
        .reduce(
          (devices, key) => {
            const filteredDevices = devices[key].filter(device => device.isShow);
            return { ...devices, [key]: filteredDevices };
          },
          { ...this.state },
        );
      const { list, window, ...data } = renderData;
      const windowEntry = this.state.window[0];
      onDataChanged({
        ...data,
        windowWidth: windowEntry.size.width,
        windowHeight: windowEntry.size.height,
        windowMode: this.props.windowMode || windowEntry.shape || 'rectangle',
        windowVertices: windowEntry.vertices || [],
      });
    }
  }

  getCurrentData() {
    const renderData = Object.keys(this.state)
      .filter(key => key.toLowerCase().indexOf('devices') !== -1)
      .reduce(
        (devices, key) => {
          const filteredDevices = devices[key].filter(device => device.isShow);
          return { ...devices, [key]: filteredDevices };
        },
        { ...this.state },
      );
    const { list, window, ...data } = renderData;
    const windowEntry = this.state.window[0];
    return {
      ...data,
      windowWidth: windowEntry.size.width,
      windowHeight: windowEntry.size.height,
      windowMode: this.props.windowMode || windowEntry.shape || 'rectangle',
      windowVertices: windowEntry.vertices || [],
    };
  }

  render() {
    const { t } = this.props;
    return (
      <div style={{ textAlign: 'right' }}>
        <ButtonGroup>
          <Button onClick={() => this.addDevice('left', 'leftDevices')}>{t('add left device')}</Button>
          <Button onClick={() => this.addDevice('right', 'rightDevices')}>{t('add right device')}</Button>
          <Button onClick={() => this.addDevice('top', 'topDevices')}>{t('add top device')}</Button>
          <Button onClick={() => this.addDevice('front', 'frontDevices')}>{t('add front device')}</Button>
        </ButtonGroup>
        <DataTable
          columns={this.columns}
          sortable={false}
          data={this.state.list.map(item => this.state[item.name][item.index]).filter(item => !!item)}
          onActionDelete={({ original, index }) => this.removeDevice(index)}
          shouldShowActionMenu={(name, row) => {
            switch (name) {
              case DataTable.ACTION_CUSTOM:
                return row.original.name.toLowerCase() !== 'window';
              case DataTable.ACTION_DELETE:
                return row.original.name.toLowerCase() !== 'window';
              default:
                return true;
            }
          }}
        />
      </div>)
  }
}

export default translate('translations')(SCControlPanelTable);



// WEBPACK FOOTER //
// ./src/components/sc_editor/SCControlPanelTable.js